<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useDark } from '@vueuse/core';

export type LogoVariants = 'default' | 'payoff' | 'payoff-only';
export type LogoColors = 'primary' | 'secondary' | 'accent' | 'black' | 'white';

export interface LogoProps {
  variant?: LogoVariants;
  color?: LogoColors;
  icon?: boolean;
  logoPrefix?: string;
  cdnUrl?: string;
}

const props = withDefaults(defineProps<LogoProps>(), {
  variant: 'default',
  color: 'accent',
  icon: false,
  logoPrefix: 'lo_econnect',
  cdnUrl: 'https://cdn.econnect.eu/media' // Default CDN URL with media path
});

// Use props instead of runtime config for pure Vue compatibility
const { logoPrefix, cdnUrl } = props;

const isDark = useDark();
const isMounted = ref(false);

onMounted((): void => {
  isMounted.value = true;
});

const variantSegment = computed((): string =>
  props.variant && props.variant !== 'default' ? `-${props.variant}` : ''
);

const colorSegment = computed((): string => `-${props.color}`);

const modeSegment = computed((): string => `-${isDark.value ? 'light' : 'dark'}`);

const logoUrl = computed((): string =>
  props.icon
    ? `${cdnUrl}/logos/${logoPrefix}-icon${colorSegment.value}.svg`
    : `${cdnUrl}/logos/${logoPrefix}${variantSegment.value}${colorSegment.value}${modeSegment.value}.svg`
);
</script>

<template>
  <div>
    <e-vector
      v-if="isMounted"
      :key="logoUrl"
      class="e-logo"
      lazy
      :url="logoUrl"
    />
    <div
      v-else
      class="h-full rounded-xl bg-black/5 animate-pulse"
    ></div>
  </div>
</template>
